import * as React from 'react'
import {useLocation, NavLink, useNavigate} from 'react-router-dom'
import {
  initialState,
  reducer,
  getMenuItems,
  setAndOpenMenuItem,
  MenuState,
  MainMenuState,
} from './mainMenuHelpers'
import {
  Container,
  MenuItem,
  MenuItemContainer,
  MenuBanner,
  MenuNameAndIcon,
  CollapseToggle,
  MainMenuTitle,
  TooltipLabel,
  SubMenuContainer,
  CollapseIcon,
  IconHolder,
  LogOutButton,
  BrandingImage,
  BottomSection,
  MobileMenuContainer as MobileHeader,
  HomeIcon,
  Title,
  BannerLink,
} from './mainMenuStyles'
import {useStore} from 'common/useStore'
import {
  LEFT_MENU_WIDTH_COLLAPSED,
  LEFT_MENU_WIDTH_EXPANDED,
  AZURE_IMAGE_STORAGE_ACCOUNT_URL_BASE,
  APP_FLAVOR,
  QUERIES,
} from 'common/constants'
import GeoforceAscend from 'images/geoforceascendlogo.svg'
// import TemedaLogo from 'images/temeda-logo-2020.png'
import TemedaLogo from 'images/Geoforce-Temeda-Logo.png'
import {
  ArrowCircleLeft,
  CaretDown,
  CaretUp,
  ArrowUpRight,
  House,
  List,
  SignOut,
  X,
  CaretRight,
  CaretLeft,
  // Headset,
} from 'phosphor-react'
import ConversationToast from 'routingAndDispatch/conversations/Popups/ConversationToast'
// import ConversationWidget from 'routingAndDispatch/conversations/ConversationWidget'
import Tooltip from 'common/components/Tooltip'
import CompanyDropdownMenu from './CompanyDropdownMenu'
import styled from 'styled-components/macro'

import FlyoutMenu from './FlyoutMenu'
import {useQueryClient} from '@tanstack/react-query'
import {ascendColors} from './GlobalStyles'

const DesktopOnly = styled.div`
  display: none;
  @media ${QUERIES.tabletAndUp} {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  svg {
    max-width: 250px;
    max-height: 70px;
    height: auto;
  }
`

const MobileOnly = styled(MenuBanner)`
  width: 100%;
  @media ${QUERIES.tabletAndUp} {
    display: none;
  }
`

type MainMenuProps = {
  title: string
  showMainMenuHeader: boolean
  isMobile: boolean
  className?: string | null
}

const MainMenu = ({
  title,
  showMainMenuHeader,
  isMobile,
  className = null,
}: MainMenuProps) => {
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    showNav: !isMobile,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const userConfig = useStore(state => state.userConfig)
  const setLeftMenuWidth = useStore(state => state.setLeftMenuWidth)
  const cache = useQueryClient()

  const toggleCollapse = () => {
    if (state.collapse) {
      setLeftMenuWidth(LEFT_MENU_WIDTH_EXPANDED)
    } else {
      setLeftMenuWidth(LEFT_MENU_WIDTH_COLLAPSED)
    }
    dispatch({type: 'setState', data: {collapse: !state.collapse}})
  }

  const toggleMenuItem = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMobile) {
      const name = e.currentTarget.dataset.name
      const newMenuItems = state.menuItems.map(item => {
        if (item.name === name) {
          item.open = !item.open
        }

        return item
      })
      dispatch({type: 'setState', data: {menuItems: newMenuItems}})
    } else {
      if (!state.subMenuOpen.isOpen) {
        const name = e.currentTarget.dataset.name
        let newMenuItems = state.menuItems.find(
          item => item.name === name,
        )?.subMenu
        if (newMenuItems && name) {
          const displayName = `${name.charAt(0).toUpperCase()}${name.slice(1)}`
          dispatch({
            type: 'setState',
            data: {
              subMenuOpen: {isOpen: true, name: displayName},
              menuItems: newMenuItems as MenuState[],
            },
          })
          // } else if (e.currentTarget.dataset.link) {
          //   newMenuItems = getMenuItems(userConfig, dispatch, state)
          //   dispatch({
          //     type: 'setState',
          //     data: {
          //       subMenuOpen: {isOpen: false, name: ''},
          //       menuItems: newMenuItems,
          //       showNav: false,
          //     },
          //   })
        }
        // } else if (e.currentTarget.dataset.link) {
        //   const newMenuItems = getMenuItems(userConfig, dispatch)
        //   dispatch({
        //     type: 'setState',
        //     data: {
        //       subMenuOpen: {isOpen: false, name: ''},
        //       menuItems: newMenuItems,
        //       showNav: false,
        //     },
        //   })
      } else {
        if (userConfig) {
          dispatch({
            type: 'setState',
            data: {
              subMenuOpen: {isOpen: false, name: ''},
              menuItems: getMenuItems(userConfig),
              showNav: false,
            },
          })
        }
      }
    }
    if (state.collapse) {
      toggleCollapse()
    }
  }

  const logOutClick = () => {
    document.body.style.setProperty('--primary', ascendColors.primary)
    document.body.style.setProperty('--secondary', ascendColors.secondary)
    document.body.style.setProperty('--tertiary', ascendColors.tertiary)
    navigate('/logout')
  }

  React.useEffect(() => {
    if (state.idUser) {
      if (
        location.pathname.startsWith('/maintenance') &&
        state.activeTab !== 'maintenance'
      ) {
        setAndOpenMenuItem('maintenance', dispatch, state.menuItems)
      } else if (
        location.pathname.startsWith('/reports') &&
        state.activeTab !== 'reports'
      ) {
        setAndOpenMenuItem('reports', dispatch, state.menuItems)
      } else if (
        location.pathname.startsWith('/alert') &&
        state.activeTab !== 'alerts'
      ) {
        setAndOpenMenuItem('alerts', dispatch, state.menuItems)
      } else if (
        location.pathname.startsWith('/map') &&
        state.activeTab !== 'map'
      ) {
        setAndOpenMenuItem('map', dispatch, state.menuItems)
      } else if (
        location.pathname.startsWith('/dashboards') &&
        state.activeTab !== 'dashboards'
      ) {
        setAndOpenMenuItem('dashboards', dispatch, state.menuItems)
      } else if (
        (location.pathname.startsWith('/heirarchy') ||
          location.pathname.startsWith('/contacts') ||
          location.pathname.startsWith('/integrations') ||
          location.pathname.startsWith('/customizeviews')) &&
        state.activeTab !== 'account'
      ) {
        setAndOpenMenuItem('account', dispatch, state.menuItems)
      } else if (
        (location.pathname.startsWith('/asset') ||
          location.pathname.startsWith('/cameras') ||
          location.pathname.startsWith('/driver') ||
          location.pathname.startsWith('/fuel-card') ||
          location.pathname.startsWith('/group') ||
          location.pathname.startsWith('/location') ||
          location.pathname.startsWith('/project') ||
          location.pathname.startsWith('/sensor') ||
          // location.pathname.startsWith('/timesheets') ||
          location.pathname.startsWith('/devices') ||
          location.pathname.startsWith('/user') ||
          location.pathname.startsWith('/zone')) &&
        state.activeTab !== 'admin'
      ) {
        setAndOpenMenuItem('admin', dispatch, state.menuItems)
      } else if (
        location.pathname.startsWith('/routing') &&
        state.menuItems.find(item => item.name === 'routeoptimization') &&
        state.activeTab !== 'routeoptimization'
      ) {
        setAndOpenMenuItem('routeoptimization', dispatch, state.menuItems)
      }
    }
  }, [location.pathname, state.activeTab, state.menuItems, state.idUser])

  React.useEffect(() => {
    if (
      userConfig?.idUser &&
      state.idUser !== userConfig?.idUser &&
      !isMobile
    ) {
      const newData: Partial<MainMenuState> = {}
      newData.menuItems = getMenuItems(userConfig, cache)
      newData.idUser = userConfig.idUser
      if (userConfig.Branding.Logo) {
        newData.logoSource = `${AZURE_IMAGE_STORAGE_ACCOUNT_URL_BASE}/images/${userConfig.Branding.Logo}`
      } else {
        newData.logoSource =
          APP_FLAVOR === 'ascend' ? GeoforceAscend : TemedaLogo
      }
      dispatch({
        type: 'setState',
        data: newData,
      })
    }
  }, [state.idUser, userConfig, userConfig?.idUser, isMobile, cache])

  if (showMainMenuHeader === false || !state.menuItems) {
    return null
  }
  return state.showNav || !isMobile ? (
    <Container collapse={String(state.collapse)} className={className || ''}>
      <MenuBanner>
        <DesktopOnly>
          {state.collapse ? (
            <CollapseIcon
              onClick={() => navigate('/map')}
              data-testid="nav-home"
            >
              <House weight="fill" color="var(--primary)" />
            </CollapseIcon>
          ) : (
            <BannerLink href="/map" data-testid="nav-logo">
              <img src={state.logoSource} />
            </BannerLink>
          )}
        </DesktopOnly>
        <MobileOnly>
          {!state.subMenuOpen.isOpen ? (
            <>
              <IconHolder
                onClick={() =>
                  dispatch({type: 'setState', data: {showNav: false}})
                }
              >
                <X size={25} color={'var(--primary)'} weight="fill" />
              </IconHolder>
              <div>Menu</div>
              <LogOutButton onClick={logOutClick}>
                Logout <SignOut size={20} />
              </LogOutButton>
            </>
          ) : (
            <>
              <IconHolder
                onClick={() => {
                  dispatch({
                    type: 'setState',
                    data: {subMenuOpen: {isOpen: false, name: ''}},
                  })
                  const newMenuItems = getMenuItems(userConfig, cache)
                  dispatch({type: 'setState', data: {menuItems: newMenuItems}})
                }}
              >
                <CaretLeft size={20} color={'var(--primary)'} /> Menu
              </IconHolder>
              <div>{state.subMenuOpen.name}</div>
              <div> </div> {/*keeps spacing uniform*/}
            </>
          )}
        </MobileOnly>
      </MenuBanner>
      {state.logoSource && (
        <MobileOnly>
          <BrandingImage>
            {!state.subMenuOpen.isOpen && (
              <a href="/map">
                <img src={state.logoSource} />
              </a>
            )}
          </BrandingImage>
        </MobileOnly>
      )}
      <MenuItemContainer>
        {state.menuItems.map(item => (
          <React.Fragment key={item.name}>
            {item.link ? (
              <Tooltip
                tooltip={
                  <TooltipLabel>
                    <span>{item.text}</span>
                    {item.external && (
                      <ArrowUpRight size={16} style={{display: 'inline'}} />
                    )}
                  </TooltipLabel>
                }
                position={'right center'}
                disabled={!state.collapse}
                trigger={
                  <NavLink
                    to={item.link}
                    target={item.newWindow ? '_blank' : ''}
                  >
                    <MenuItem
                      open={Boolean(item.open)}
                      name={item.name}
                      fontOnPrimary={ascendColors.fontOnPrimary}
                      fontOnSecondary={ascendColors.fontOnSecondary}
                      isActive={Boolean(item.isActive)}
                      onClick={isMobile ? toggleMenuItem : () => null}
                      data-link={true}
                      data-name={item.name}
                      data-testid={`nav-${item.name}`}
                    >
                      <MenuNameAndIcon
                        open={Boolean(item.open)}
                        isActive={Boolean(item.isActive)}
                      >
                        {item.icon}
                        {!state.collapse && (
                          <MainMenuTitle>{item.text}</MainMenuTitle>
                        )}
                      </MenuNameAndIcon>
                      {item.external && !state.collapse && (
                        <ArrowUpRight size={16} />
                      )}
                    </MenuItem>
                  </NavLink>
                }
              />
            ) : (
              <Tooltip
                tooltip={item.text}
                position={'right center'}
                disabled={!state.collapse}
                trigger={
                  <MenuItem
                    open={item.open}
                    data-name={item.name}
                    onClick={toggleMenuItem}
                    name={item.name}
                    isActive={item.isActive}
                    data-testid={`nav-${item.name}`}
                    fontOnPrimary={ascendColors.fontOnPrimary}
                    fontOnSecondary={ascendColors.fontOnSecondary}
                  >
                    <MenuNameAndIcon open={item.open} isActive={item.isActive}>
                      {item.icon}
                      {!state.collapse && (
                        <MainMenuTitle>{item.text}</MainMenuTitle>
                      )}
                    </MenuNameAndIcon>
                    {!isMobile ? (
                      !state.collapse && (
                        <>
                          {item.open ? (
                            <CaretUp size={16} />
                          ) : (
                            <CaretDown size={16} />
                          )}
                        </>
                      )
                    ) : (
                      <CaretRight size={20} />
                    )}
                  </MenuItem>
                }
              />
            )}
            {item.open && !isMobile && item.subMenu && (
              <SubMenuContainer
                fontOnPrimary={ascendColors.fontOnPrimary}
                fontOnSecondary={ascendColors.fontOnSecondary}
              >
                {item.subMenu.map((subitem, index) => (
                  <FlyoutMenu
                    key={`${subitem.name}-${index}`}
                    subitem={subitem}
                    collapse={state.collapse}
                    dataTestId={`nav-${item.name}-${subitem.name
                      .replace(/[^a-zA-Z0-9]/g, '')
                      .toLowerCase()}`}
                  />
                ))}
              </SubMenuContainer>
            )}
          </React.Fragment>
        ))}
      </MenuItemContainer>
      <MobileOnly>
        <BottomSection>
          {!state.subMenuOpen.isOpen && (
            <CompanyDropdownMenu setHasDropdown={() => null} />
          )}
        </BottomSection>
      </MobileOnly>
      <DesktopOnly>
        <CollapseToggle
          onClick={() => toggleCollapse()}
          collapse={String(state.collapse)}
          data-testid={state.collapse ? 'nav-expand' : 'nav-collapse'}
        >
          <ArrowCircleLeft
            color={'var(--tertiary)'}
            weight={'fill'}
            size={30}
            mirrored={state.collapse}
          />
        </CollapseToggle>
      </DesktopOnly>
      <ConversationToast />
    </Container>
  ) : (
    <MobileHeader>
      <HomeIcon onClick={() => navigate('/map')}>
        <House weight="fill" color="var(--primary)" size={25} />
      </HomeIcon>
      <Title>{title}</Title>
      <IconHolder
        onClick={() => dispatch({type: 'setState', data: {showNav: true}})}
      >
        <List size={30} color={'var(--primary)'} />
      </IconHolder>
    </MobileHeader>
  )
}
export default MainMenu
